import { FC, useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { usePanelParams } from '../../usePanelParams';
import { AlgorithmValidFormDataType } from '../schema/types';
import { AlgorithmTargetFormModal } from './AlgorithmTargetFormModal';
import { AlgorithmTargets } from './AlgorithmTargets';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { AlgorithmTargetsHeader } from './AlgorithmTargetsHeader';
import {
  AlgorithmTargetFormDataType,
  AlgorithmTargetsFormDataType,
} from './schema/types';
import { useMutationErrors } from '../../../common/queryClient';
import { useMutateAlgorithmTargetQuery } from './query/useMutateAlgorithmTargetQuery';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import validationTranslations from '../../translations/validation.translations.json';
import { useGetAlgorithmTargetsQuery } from './query/useGetAlgorithmTargetsQuery';
import { warningAlgorithmDirty } from '../../../common/form';
import { getMaxRuleCode, getMaxTargetSeqNr } from './utils';

const { addTranslations, t } = i18nNsFactory('algorithm-form-target');
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

interface AlgorithmFormTargetsProps {
  isAlgorithmDirty: boolean;
  expanded: boolean;
  onToggle?: (expanded: boolean) => void;
  onChange?: (changedTargets: AlgorithmTargetsFormDataType) => void;
  targetId?: number;
}

export const AlgorithmFormTargets: FC<AlgorithmFormTargetsProps> = (props) => {
  const { onChange, ...rest } = props;
  const id = useWatch<AlgorithmValidFormDataType, 'id'>({ name: 'id' });

  const {
    data: algorithmTargets,
    refetch,
    isLoading,
  } = useGetAlgorithmTargetsQuery({
    id,
  });

  const handleSuccess = useCallback(async () => {
    const { data: changedTargets } = await refetch();
    if (changedTargets) {
      onChange?.(changedTargets);
    }
  }, [refetch, onChange]);

  if (isLoading) {
    return <AlgorithmTargetsHeader isLoading="add" onAddButtonClick={noop} />;
  }

  if (!algorithmTargets) {
    return null;
  }

  return (
    <AlgorithmFormTargetsInner
      {...rest}
      targets={algorithmTargets.targets}
      onSuccess={handleSuccess}
    />
  );
};

const noop = () => {};

interface AlgorithmFormTargetsInnerProps {
  isAlgorithmDirty: boolean;
  expanded: boolean;
  onToggle?: (expanded: boolean) => void;
  targetId?: number;
  targets: AlgorithmTargetFormDataType[];
  onSuccess: () => void;
}

export const AlgorithmFormTargetsInner: FC<AlgorithmFormTargetsInnerProps> = (
  props,
) => {
  const { isAlgorithmDirty, targets, onSuccess } = props;
  const [algorithmId, algorithmTypeCode] = useWatch({
    name: ['id', 'typeCode'],
  });

  const [modal, setModal] = useState<{
    type: 'form' | 'graph';
    targetId?: number;
    clone?: boolean;
  } | null>(props.targetId ? { type: 'form', targetId: props.targetId } : null);

  const { mutateAsync } = useMutateAlgorithmTargetQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const [, setPanelParams] = usePanelParams();
  const toggleTargetPanel = useCallback(
    (targetId?: number) => {
      setPanelParams({ algorithmId, targetId });
    },
    [setPanelParams, algorithmId],
  );

  const handleEdit = useCallback(
    (
      targetId: number | undefined = undefined,
      type: 'form' | 'graph' = 'form',
    ) => {
      if (isAlgorithmDirty) {
        warningAlgorithmDirty();
      } else {
        setModal({ type, targetId });
        toggleTargetPanel(targetId);
      }
    },
    [isAlgorithmDirty, toggleTargetPanel],
  );

  const handleEditGraph = useCallback(
    (targetId: number | undefined = undefined) => {
      handleEdit(targetId, 'graph');
    },
    [handleEdit],
  );

  const handleClone = useCallback(
    (targetId: number) => {
      if (isAlgorithmDirty) {
        warningAlgorithmDirty();
      } else {
        setModal({ type: 'form', targetId, clone: true });
        toggleTargetPanel();
      }
    },
    [isAlgorithmDirty, toggleTargetPanel],
  );

  const handleSuccess = useCallback(async () => {
    onSuccess();
    setModal(null);
    toggleTargetPanel();
  }, [toggleTargetPanel, onSuccess]);

  const handleDelete = useCallback(
    (id: number) => {
      if (isAlgorithmDirty) {
        warningAlgorithmDirty();
      } else {
        clearMutationErrors();
        return mutateAsync({
          id,
        })
          .then(handleSuccess)
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }
    },
    [
      isAlgorithmDirty,
      mutateAsync,
      clearMutationErrors,
      setMutationErrors,
      handleSuccess,
    ],
  );

  const handleCancel = useCallback(() => {
    setModal(null);
    toggleTargetPanel();
  }, [toggleTargetPanel]);

  const editable = useMemo(() => {
    if (!modal) {
      return undefined;
    }

    const target = targets.find(({ id }) => id === modal.targetId);

    if (!modal.clone || !target) {
      return target;
    }

    const maxTargetSeqNr = getMaxTargetSeqNr(targets);
    const maxRuleCode = getMaxRuleCode(
      targets.flatMap((target) => target.rules),
    );

    return {
      ...target,
      id: -1,
      sequenceNumber: maxTargetSeqNr + 1,
      rules: target.rules.map((rule, index) => ({
        ...rule,
        id: -1,
        ruleCode: String(maxRuleCode + 1 + index),
        items: rule.items.map((item) => ({
          ...item,
          id: -1,
        })),
      })),
    };
  }, [modal, targets]);

  const handleModalTypeChange = useCallback(
    (isGraphic: boolean) =>
      setModal((modal) =>
        modal ? { ...modal, type: isGraphic ? 'graph' : 'form' } : null,
      ),
    [],
  );

  return (
    <>
      <AlgorithmTargets
        {...props}
        algorithmId={algorithmId}
        targetId={modal?.targetId}
        mutationErrors={mutationErrors}
        onAddButtonClick={handleEdit}
        onGraphButtonClick={handleEditGraph}
        onEditButtonClick={handleEdit}
        onCloneButtonClick={handleClone}
        onDeleteButtonClick={handleDelete}
      />
      {modal && (
        <AlgorithmTargetFormModal
          algorithmId={algorithmId}
          algorithmTypeCode={algorithmTypeCode}
          targets={targets}
          editable={editable}
          onSuccess={handleSuccess}
          onClose={handleCancel}
          isGraphic={modal?.type === 'graph'}
          onIsGraphicChange={handleModalTypeChange}
        />
      )}
    </>
  );
};
